<template>
  <div
    id="app"
    :data-tenant="tenantConfigName"
    :class="[quoteJourney ? 'quote-journey' : '', `${currentRoute}-page`]"
  >
    <b-navbar
      ref="navbar"
      :close-on-click="!quoteJourney"
      :mobile-burger="!quoteJourney"
    >
      <template slot="brand">
        <div
          v-if="(aggregatorClient || affiliate) && quoteJourney"
          class="agg-to-broker"
        >
          <div
            v-if="aggregatorClient"
            class="agg-logo"
          >
            <img
              v-if="aggregatorClient.img"
              alt="Aggregator Logo"
              :src="`data:image/png;base64,${aggregatorClient.img}`"
            >
            <span v-else><b>{{ aggregatorClient.name }}</b></span>
          </div>
          <div
            v-else-if="affiliate"
            class="agg-logo"
          >
            <img
              v-if="affiliate.logo"
              alt="Affiliate Logo"
              :src="affiliate.logo.toString()"
            >
            <span v-else><b>{{ affiliate.name }}</b></span>
          </div>
          <div class="agg-arrow">
            <i class="fas fa-long-arrow-alt-right" />
          </div>
          <div class="brand-logo">
            <a
              :href="logoUrl"
              target="_blank"
            >
              <img
                v-if="headerLogoPath"
                alt="Logo"
                :src="headerLogoPath"
              >
            </a>
          </div>
        </div>
        <div
          v-else
          class="level site-logo"
        >
          <b-navbar-item>
            <a
              :href="logoUrl"
              target="_blank"
            >
              <img
                v-if="headerLogoPath"
                alt="Logo"
                :src="headerLogoPath"
              >
            </a>
          </b-navbar-item>
        </div>
        <div
          v-if="headerText"
          class="navbar-contact"
        >
          <div class="level">
            <IgniteMarkdown class="content">
              {{ headerText }}
            </IgniteMarkdown>
          </div>
        </div>
      </template>
      <template slot="end">
        <div
          v-if="!quoteJourney && portalEnabled && account"
          class="navbar-item navbar-item-wrapper"
        >
          <button
            v-if="referAFriend.enabled && account.customerReference"
            class="button refer-a-friend-button"
            @click="openReferAFriend"
          >
            Refer a Friend
          </button>
          <span class="router-links">
            <router-link
              class="navbar-item"
              :to="dashboardRoute"
            >
              Dashboard
            </router-link>
            <router-link
              class="navbar-item"
              :to="quotesRoute"
            >
              Quotes
            </router-link>
            <router-link
              class="navbar-item"
              :to="policiesRoute"
            >
              Policies
            </router-link>
            <router-link
              class="navbar-item"
              :to="claimsRoute"
            >
              Claims
            </router-link>

            <template v-for="(a, i) in actions">
              <LinkConfigButton
                :key="i"
                class="navbar-item"
                :config="a"
                :is-anchor="true"
              />
            </template>
          </span>

          <div class="navbar-item navbar-item-wrapper">
            <div class="buttons">
              <b-dropdown hoverable>
                <b-button
                  slot="trigger"
                  class="navbar-button"
                >
                  {{ headerAccountText }}
                  <b-icon icon="menu-down" />
                </b-button>
                <b-dropdown-item custom>
                  Logged in as <b>{{ `${account.firstName} ${account.lastName}` }}</b>
                </b-dropdown-item>
                <hr class="dropdown-divider">
                <b-dropdown-item has-link>
                  <router-link :to="editAccountRoute">
                    {{ editAccountText }}
                  </router-link>
                </b-dropdown-item>
                <b-dropdown-item @click="logout">
                  Logout
                </b-dropdown-item>
              </b-dropdown>
              <b-button
                v-if="specialButton"
                type="is-primary"
                @click="specialButtonClick"
              >
                {{ specialButton.name }}
              </b-button>
            </div>
          </div>
        </div>
        <div v-else-if="quoteJourney">
          <b-steps
            v-if="steps.length > 0"
            v-model="currentStep"
            animated
            :has-navigation="false"
            size="is-small"
          >
            <b-step-item
              v-for="(s, i) in steps"
              :key="i"
              :label="s.label"
              :icon="s.icon"
              :icon-pack="s.pack"
              :clickable="false"
            />
          </b-steps>
        </div>
        <div
          v-else
          class="navbar-item navbar-item-wrapper"
        >
          <div class="navbar-item navbar-item-wrapper">
            <div class="buttons">
              <b-button
                v-for="(product, pIndex) in products"
                :key="product.uniqueId"
                :index="pIndex"
                :label="getInsureProductActionLabel(product)"
                @click="goToProductRoute(product)"
              />
            </div>
          </div>
        </div>
      </template>
    </b-navbar>

    <router-view class="app-content" />

    <TheFooter
      :footer-logo-path="footerLogoPath"
      :footer="footer"
      :tenant-name="tenantFriendlyName"
      :social-links="socialLinks"
      :footer-links="footerLinks"
    />
  </div>
</template>

<script lang="ts">
import type { RawLocation } from 'vue-router'
import type ProductEnum from './services/enums/ProductEnum'
import type Account from './services/models/Account'
import type AggregatorClient from './services/models/AggregatorClient'
import type Product from './services/models/Product'
import type { TenantConfigName } from './services/types/TenantConfigName'
import type { Dictionary } from './types'
import type Step from './view-models/Step'
import type { LinkConfig } from '@/configurations/config-objects/IConfiguration'
import type { ILinkConfigSpecification } from '@/configurations/config-objects/LinkConfigSpecification'
import { ModalProgrammatic } from 'buefy'
import { Component, Vue, Watch } from 'vue-property-decorator'
import LinkConfigModal from '@/components/LinkConfigModal.vue'
import LinkConfigFactory from '@/configurations/LinkConfigFactory'
import Routes from '@/constants/Routes'
import DisplayMode from './constants/DisplayMode'
import { goToCustomerPortalJourneyForProduct } from './customer-portal'
import useAccountStore from './stores/account-store'
import useAggregatorStore from './stores/aggregator-store'

interface AnalyticsWindow extends Window {
  dataLayer: Dictionary<string>[]
  injectedEnv: Dictionary<string>
}

declare const window: AnalyticsWindow

@Component({
  components: {
    IgniteMarkdown: () => import('@/components/controls/IgniteMarkdown.vue'),
    TheFooter: () => import('@/components-ignite/TheFooter.vue'),
    LinkConfigButton: () => import('@/components/LinkConfigButton.vue'),
  },
})
export default class App extends Vue {
  quoteJourney = false

  currentStep = 0

  steps: Step[] = []

  specialButton: LinkConfig | null = this.$configurations.specialButton

  buildNumber = import.meta.env.VUE_APP_BUILDNUMBER

  products: Product[] = []

  get currentRoute(): string | null {
    return this.$route.name ?? null
  }

  get dashboardRoute(): RawLocation {
    return { name: Routes.home }
  }

  get quotesRoute(): RawLocation {
    return { name: Routes.quotes }
  }

  get policiesRoute(): RawLocation {
    return { name: Routes.policies }
  }

  get claimsRoute(): RawLocation {
    return this.$configurations.manageClaimsEnabled ? { name: Routes.manageClaims } : { name: Routes.claims }
  }

  get editAccountRoute(): RawLocation {
    return { name: Routes.account }
  }

  get actions(): LinkConfig[] {
    return this.$configurations.accountHeaderActions
  }

  get referAFriend() {
    return this.$configurations.referAFriend
  }

  openReferAFriend(): void {
    (this.$refs.navbar as any)?.closeMenu?.()

    const closeButton: ILinkConfigSpecification = {
      name: 'Close',
      action: { type: 'emit', url: 'close', dialogConfig: null, integrationId: null },
      showOn: [],
    }

    ModalProgrammatic.open({
      parent: this,
      component: LinkConfigModal,
      hasModalCard: true,
      canCancel: ['escape', 'outside'],
      props: {
        referAFriend: {
          customerReference: this.account!.customerReference,
          defaultProduct: this.referAFriend.defaultProduct,
        },
        buttons: [LinkConfigFactory.create(closeButton)],
      },
    })
  }

  goToProductRoute(product: Product): void {
    (this.$refs.navbar as any)?.closeMenu?.()

    if (this.getInsureProductActionForProduct(product)) {
      const productOverrideUrl = this.getInsureProductActionForProduct(product)?.overrideUrl
      if (productOverrideUrl) {
        window.location.href = productOverrideUrl
        return
      }
    }

    if (this.$configurations.newCustomerPortalForProduct(product.uniqueId as ProductEnum)) {
      goToCustomerPortalJourneyForProduct(product.uniqueId as ProductEnum, 'nb')
      return
    }

    this.$router.push({
      name: Routes.productJourney,
      params: { product: product.uniqueId },
    })
  }

  get headerLogoPath(): string | null {
    return this.brandingProduct
      ? this.$configurations.headerLogoPathForProduct(this.brandingProduct) ?? this.$configurations.headerLogoPath
      : this.$configurations.headerLogoPath
  }

  get footerLogoPath(): string | null {
    return this.brandingProduct
      ? this.$configurations.footerLogoPathForProduct(this.brandingProduct) ?? this.$configurations.footerLogoPath
      : this.$configurations.footerLogoPath
  }

  get footerLinks(): LinkConfig[] | null {
    return this.brandingProduct && this.$utils.useAlternateBranding(this.brandingProduct)
      ? this.$configurations.footerLinksForProduct(this.brandingProduct)
      : this.$configurations.footerLinks
  }

  get footer(): string {
    return this.brandingProduct && this.$utils.useAlternateBranding(this.brandingProduct)
      ? this.$configurations.footerTextForProduct(this.brandingProduct) ?? ''
      : this.$configurations.footer
  }

  get tenantFriendlyName(): string {
    return this.$utils.getTenantFriendlyName(this.brandingProduct, this.$configurations)
  }

  getInsureProductActionForProduct(product: Product): { label: string, overrideUrl: string | null } | null {
    return this.$configurations.insureProductAction(product.uniqueId as ProductEnum) ?? null
  }

  getInsureProductActionLabel(product: Product): string {
    return this.getInsureProductActionForProduct(product)?.label ?? `Insure a ${product.displayName}`
  }

  get tenantConfigName(): TenantConfigName {
    return this.$configurations.tenantConfigName
  }

  get logoUrl(): string {
    return this.$configurations.logoUrl
  }

  get headerText(): string {
    return this.$configurations.headerText
  }

  get socialLinks(): LinkConfig[] {
    return this.$configurations.socialLinks
  }

  get portalEnabled(): boolean {
    return this.$configurations.features.portal
  }

  get account(): Account | null {
    const accountStore = useAccountStore()
    if (accountStore.account) {
      this.$chat.setSession(accountStore.account)
      this.$analytics.identifyUser(accountStore.account, window)
    }
    return accountStore.account
  }

  get headerAccountText(): string {
    return this.$configurations.headerAccountText
  }

  get editAccountText(): string {
    return this.$configurations.editAccountText
  }

  get aggregatorClient(): AggregatorClient | null {
    const aggregatorStore = useAggregatorStore()
    return aggregatorStore.client
  }

  get affiliate(): { name: string, logo: URL | null } | null {
    const aggregatorStore = useAggregatorStore()
    const aff = aggregatorStore.affiliate

    return !aff
      ? null
      : {
          name: aff.name,
          logo: aff.logoFile ? this.$services.documentService.generateImageURL(aff.logoFile) : null,
        }
  }

  private product: ProductEnum | null = null
  private brandingProduct: ProductEnum | null = null

  get currentProduct(): ProductEnum | null {
    return this.product
  }

  set currentProduct(val: ProductEnum | null) {
    this.product = val
    this.updateSteps()
  }

  @Watch('currentRoute', { deep: true })
  async routeChanged(): Promise<void> {
    document.title = this.$route.meta?.title ?? document.title
    this.brandingProduct = this.$route.params.product as ProductEnum | null ?? this.$route.query.product as ProductEnum | null

    await this.$utils.handleBrandSwitching(this.brandingProduct)
    document.title = this.$utils.getPageTitle(document.title, this.$configurations, this.brandingProduct)

    this.quoteJourney = this.$route.meta?.displayMode === DisplayMode.journey || this.$route.matched.some(r => r.meta.displayMode === DisplayMode.journey)
    this.currentProduct = this.$route.params.product as ProductEnum | undefined ?? this.currentProduct
    this.updateSteps()
  }

  async created(): Promise<void> {
    this.$utils.checkConsent(this.$configurations.cookiePolicyNotice)

    this.routeChanged()

    const response = await this.$services.productService.getAll()
    this.products = response.results

    // Google Optimize Patch
    const observer = new MutationObserver(() => window.dataLayer?.push({ event: 'optimize.activate' }))
    observer.observe(this.$el, { childList: true, subtree: true })
  }

  updateSteps(): void {
    if (this.currentRoute && this.currentProduct) {
      this.steps = this.$configurations.stepsForProduct(this.currentProduct, this.$route)

      const index = this.steps.findIndex(s => s.routes.includes(this.currentRoute as Routes))
      this.currentStep = index !== -1 ? index : 0
    }
  }

  specialButtonClick(): void {
    if (this.$configurations.specialButton)
      this.$configurations.specialButton.click(this)
  }

  async logout(): Promise<void> {
    await this.$services.identityProvider.logout()
    const accountStore = useAccountStore()
    accountStore.clearAccount()
    window.location.href = '/'
  }
}
</script>
